'use client';

import type {
  CommandDef,
  CommandOpenTab,
} from '@/components/command-menu/types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type DependencyList,
  type ReactElement,
  type ReactNode,
} from 'react';

type RegistryEntry = { order: number; commands: CommandDef[] };
type OpenTabRegistryEntry = { order: number; tabs: CommandOpenTab[] };

type CommandRegistryContextType = {
  register: (key: string, commands: CommandDef[]) => void;
  unregister: (key: string) => void;
  entries: ReadonlyMap<string, RegistryEntry>;
};

type OpenTabRegistryContextType = {
  registerOpenTabs: (key: string, tabs: CommandOpenTab[]) => void;
  unregisterOpenTabs: (key: string) => void;
  openTabEntries: ReadonlyMap<string, OpenTabRegistryEntry>;
};

// Registrants must not subscribe to the state their own effects update.
const CommandRegistryActionsContext = createContext<
  Pick<CommandRegistryContextType, 'register' | 'unregister'> | undefined
>(undefined);
const OpenTabRegistryActionsContext = createContext<
  | Pick<OpenTabRegistryContextType, 'registerOpenTabs' | 'unregisterOpenTabs'>
  | undefined
>(undefined);

const CommandRegistryContext = createContext<
  CommandRegistryContextType | undefined
>(undefined);
const OpenTabRegistryContext = createContext<
  OpenTabRegistryContextType | undefined
>(undefined);

export function CommandRegistryProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [entries, setEntries] = useState<Map<string, RegistryEntry>>(
    () => new Map()
  );
  const nextOrderRef = useRef(0);
  const commandOrdersRef = useRef(new Map<string, number>());
  const openTabOrdersRef = useRef(new Map<string, number>());
  const [openTabEntries, setOpenTabEntries] = useState<
    Map<string, OpenTabRegistryEntry>
  >(() => new Map());

  const register = useCallback((key: string, commands: CommandDef[]) => {
    let order = commandOrdersRef.current.get(key);
    if (order === undefined) {
      order = nextOrderRef.current;
      nextOrderRef.current += 1;
      commandOrdersRef.current.set(key, order);
    }
    setEntries((current) => {
      const next = new Map(current);
      const existing = next.get(key);
      next.set(key, {
        order: existing?.order ?? order,
        commands,
      });
      return next;
    });
  }, []);

  const unregister = useCallback((key: string) => {
    commandOrdersRef.current.delete(key);
    setEntries((current) => {
      if (!current.has(key)) return current;
      const next = new Map(current);
      next.delete(key);
      return next;
    });
  }, []);

  const registerOpenTabs = useCallback(
    (key: string, tabs: CommandOpenTab[]) => {
      let order = openTabOrdersRef.current.get(key);
      if (order === undefined) {
        order = nextOrderRef.current;
        nextOrderRef.current += 1;
        openTabOrdersRef.current.set(key, order);
      }
      setOpenTabEntries((current) => {
        const next = new Map(current);
        const existing = next.get(key);
        next.set(key, {
          order: existing?.order ?? order,
          tabs,
        });
        return next;
      });
    },
    []
  );

  const unregisterOpenTabs = useCallback((key: string) => {
    openTabOrdersRef.current.delete(key);
    setOpenTabEntries((current) => {
      if (!current.has(key)) return current;
      const next = new Map(current);
      next.delete(key);
      return next;
    });
  }, []);

  const commandActions = useMemo(
    () => ({ register, unregister }),
    [register, unregister]
  );
  const openTabActions = useMemo(
    () => ({ registerOpenTabs, unregisterOpenTabs }),
    [registerOpenTabs, unregisterOpenTabs]
  );

  const commandValue = useMemo(
    () => ({
      register,
      unregister,
      entries,
    }),
    [register, unregister, entries]
  );
  const openTabValue = useMemo(
    () => ({ registerOpenTabs, unregisterOpenTabs, openTabEntries }),
    [registerOpenTabs, unregisterOpenTabs, openTabEntries]
  );

  return (
    <CommandRegistryActionsContext.Provider value={commandActions}>
      <OpenTabRegistryActionsContext.Provider value={openTabActions}>
        <CommandRegistryContext.Provider value={commandValue}>
          <OpenTabRegistryContext.Provider value={openTabValue}>
            {children}
          </OpenTabRegistryContext.Provider>
        </CommandRegistryContext.Provider>
      </OpenTabRegistryActionsContext.Provider>
    </CommandRegistryActionsContext.Provider>
  );
}

function useCommandRegistryContext(): CommandRegistryContextType {
  const ctx = useContext(CommandRegistryContext);
  if (!ctx) {
    throw new Error(
      'Command registry hooks must be used within a CommandRegistryProvider'
    );
  }
  return ctx;
}

function useOpenTabRegistryContext(): OpenTabRegistryContextType {
  const ctx = useContext(OpenTabRegistryContext);
  if (!ctx) {
    throw new Error(
      'Open-tab registry hooks must be used within a CommandRegistryProvider'
    );
  }
  return ctx;
}

/**
 * Register contextual commands for the ⌘K menu while the calling component
 * is mounted. Labels and group headings must be pre-translated — the
 * registering surface owns its i18n namespace.
 */
export function usePageCommands(
  commands: CommandDef[],
  deps: DependencyList
): void {
  const key = useId();
  const actions = useContext(CommandRegistryActionsContext);
  if (!actions)
    throw new Error(
      'Command registry hooks must be used within a CommandRegistryProvider'
    );
  const { register, unregister } = actions;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoized = useMemo(() => commands, deps);

  useEffect(() => {
    register(key, memoized);
    return () => unregister(key);
  }, [key, memoized, register, unregister]);
}

/**
 * All currently registered commands, most recently registered group first
 * (deepest mounted component = most specific context = top of the menu).
 */
export function useRegisteredCommands(): CommandDef[] {
  const { entries } = useCommandRegistryContext();
  return useMemo(() => {
    const commands = [...entries.values()]
      .sort((a, b) => b.order - a.order)
      .flatMap((entry) => entry.commands);

    const seen = new Set<string>();
    return commands.filter((command) => {
      if (seen.has(command.id)) return false;
      seen.add(command.id);
      return true;
    });
  }, [entries]);
}

export function usePageOpenTabs(tabs: CommandOpenTab[]): void {
  const key = useId();
  const actions = useContext(OpenTabRegistryActionsContext);
  if (!actions)
    throw new Error(
      'Open-tab registry hooks must be used within a CommandRegistryProvider'
    );
  const { registerOpenTabs, unregisterOpenTabs } = actions;

  useEffect(() => {
    registerOpenTabs(key, tabs);
    return () => unregisterOpenTabs(key);
  }, [key, tabs, registerOpenTabs, unregisterOpenTabs]);
}

export function useRegisteredOpenTabs(): CommandOpenTab[] {
  const { openTabEntries } = useOpenTabRegistryContext();
  return useMemo(() => {
    const tabs = [...openTabEntries.values()]
      .sort((left, right) => right.order - left.order)
      .flatMap((entry) => entry.tabs);
    const seen = new Set<string>();
    return tabs.filter((tab) => {
      if (seen.has(tab.id)) return false;
      seen.add(tab.id);
      return true;
    });
  }, [openTabEntries]);
}
